import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Signal,
  inject,
  input,
  output,
  viewChild,
  viewChildren,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  NEVER,
  Observable,
  Subscription,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  iif,
  map,
  of,
  startWith,
  switchMap,
  take,
  throwError,
  withLatestFrom,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ConsentTextId } from '@fcom/common';
import { ConsentService } from '@fcom/common/login';
import { LocationPair } from '@fcom/common/store';
import { AppState, GeolocationService, LatLng, LocationRouteCffService } from '@fcom/core';
import { Location, LocationGeoLocData, LocationMatchData, LocationType as LocationTypeCff } from '@fcom/core-api';
import { akamaiGeolocation, isBrowserGeolocationLoading } from '@fcom/core/selectors';
import { finShare, retryWithBackoff } from '@fcom/rx';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  LoaderTheme,
  TextInputComponent,
  PopoverOptions,
  PopoverService,
} from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { isNotBlank, isPresent, stopPropagation, unsubscribe, isBlank } from '@fcom/core/utils';
import { TransportType } from '@fcom/common-booking/interfaces/utils.interface';
import { DestinationSearchService } from '@fcom/destination-search/services/destination-search.service';
import { DestinationSearchItem } from '@fcom/destination-search/interfaces/destination-search.interface';

import { GtmLocationEventType, Locations, LocationType, LocationParam, SelectorType } from '../../interfaces';
import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { BookingWidgetService } from '../../services/booking-widget.service';
import { BOOKING_WIDGET_CONTEXT, defaultWidgetPopoverOptions } from '../../constants';
import { BookingWidgetFlightService } from '../../services/booking-widget-flight.service';

@Component({
  selector: 'fin-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LocationSelectorComponent implements AfterViewInit, OnInit, OnDestroy {
  private store$ = inject<Store<AppState>>(Store);
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);
  private geoLocationService = inject(GeolocationService);
  private consentService = inject(ConsentService);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);
  private bookingWidgetService = inject(BookingWidgetService);
  private popoverService = inject(PopoverService);
  private bookingWidgetFlightService = inject(BookingWidgetFlightService);
  private destinationSearchService = inject(DestinationSearchService);

  readonly LoaderTheme = LoaderTheme;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly LocationTypeCff = LocationTypeCff;
  readonly IconPosition = IconPosition;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconButtonSize = IconButtonSize;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TransportType = TransportType;
  readonly SelectorType = SelectorType;

  readonly originPopoverOptions: PopoverOptions = {
    ...defaultWidgetPopoverOptions,
    popoverID: 'locationSelectorPopover',
    alignToLeft: true,
    popoverWidth: 456,
    disableAutoFocus: true,
  };
  readonly destinationPopoverOptions: PopoverOptions = {
    ...defaultWidgetPopoverOptions,
    popoverID: 'locationSelectorPopover',
    popoverWidth: 456,
    disableAutoFocus: true,
  };

  readonly disabled = input(false);

  readonly locationPair = input<LocationPair>();

  readonly showSeparator = input(true);

  readonly showLocateMe = input(true);

  readonly allowedOriginContinent = input<string>();

  readonly allowedDestinationContinent = input<string>();

  readonly originDisabled = input<boolean>();

  readonly destinationDisabled = input<boolean>();

  readonly showPreviousSearches = input<boolean>();

  readonly isAm = input(false);

  readonly showSuggestedDestinations = input(true);

  readonly amOrigins = input<Location[]>();

  readonly amDestinations = input<Location[]>();

  readonly isGlobalBookingWidget = input(false);

  readonly singleLocation = input(false);

  readonly originLabel = input<string>();

  readonly originPlaceholder = input<string>();

  readonly airportsOnly = input(false);

  readonly skipDefaultOriginFetch = input(false);

  readonly setLocations = output<LocationParam>();

  readonly startSearch = output<void>();

  private readonly amOrigins$ = toObservable(this.amOrigins);
  private readonly amDestinations$ = toObservable(this.amDestinations);

  modalOpen = false;
  locationType$: BehaviorSubject<'origin' | 'destination'> = new BehaviorSubject('origin');
  GtmLocationEventType$: BehaviorSubject<'locations' | 'suggestions'> = new BehaviorSubject('locations');
  defaultOrigin$: Observable<Location>;
  origin$: Observable<Location>;
  destination$: Observable<Location>;
  enablePersonalization$: Observable<boolean>;
  defaultLocations$: Observable<Location[]>;
  locationResults$: BehaviorSubject<Location[]> = new BehaviorSubject(undefined);
  locationSearchForm: FormGroup<{ locationSearch: FormControl<string> }>;
  queryString$: Observable<string>;
  hasSearch$: Observable<boolean>;
  isGeoLocating$: Observable<boolean>;
  activeIndex$ = new BehaviorSubject<number>(undefined);
  hasLocations$: Observable<boolean>;
  isLoading$: Observable<boolean>;
  directDestinations$: Observable<DestinationSearchItem[]>;

  locationsSwitched = new EventEmitter<void>();
  locationSelected = new EventEmitter<{ location: Location; locationType: LocationType }>();
  private readonly _suggestedLocations$ = toObservable(this.bookingWidgetFlightService.suggestedLocations);
  private readonly _trendingDestinations$ = toObservable(this.bookingWidgetFlightService.trendingDestinations);
  private readonly _modifyType$ = toObservable(this.bookingWidgetService.modifyType);

  locations$: Observable<Location[]>;

  private subscriptions: Subscription = new Subscription();

  readonly locationListHeading = viewChild<ElementRef>('locationListHeading');
  readonly searchInput = viewChild<TextInputComponent>('searchInput');
  readonly locationItems: Signal<readonly ElementRef[]> = viewChildren('locationItem', { read: ElementRef });
  readonly usePopoverSelectors = this.bookingWidgetService.usePopoverSelectors();

  ngOnInit(): void {
    this.isGeoLocating$ = this.store$.pipe(isBrowserGeolocationLoading());

    this.directDestinations$ = this.languageService.countryCode.pipe(
      switchMap((countryCode) => this.destinationSearchService.getDirectDestinations(countryCode))
    );

    this.defaultOrigin$ = this.store$.pipe(akamaiGeolocation()).pipe(
      switchMap((value: LatLng) =>
        this.locationRouteCffService.geolocMatchFor(value.lat, value.lng, this.languageService.localeValue)
      ),
      switchMap((location: LocationGeoLocData) =>
        this.locationRouteCffService.bestGuessFor(location.item.locationCode, this.languageService.localeValue)
      ),
      retryWithBackoff(2),
      catchError((err: unknown) => {
        return throwError(() => err);
      }),
      distinctUntilChanged(),
      finShare()
    );

    this.locationSearchForm = new FormGroup({
      locationSearch: new FormControl(''),
    });

    this.defaultLocations$ = iif(
      () => this.isAm(),
      combineLatest([this.amOrigins$, this.amDestinations$, this.locationType$]).pipe(
        map(([amOrigins, amDestinations, locationType]) => (locationType === 'origin' ? amOrigins : amDestinations))
      ),
      combineLatest([
        this._suggestedLocations$,
        this.directDestinations$,
        this._trendingDestinations$,
        this.locationType$,
      ]).pipe(
        map(([suggestedLocations, directDestinations, trendingLocations, locationType]) => {
          const directOrigins = this.mapDirectDestinations(directDestinations);
          return this.showSuggestedDestinations()
            ? locationType === 'origin'
              ? directOrigins.length
                ? directOrigins
                : suggestedLocations?.departures
              : trendingLocations
                ? trendingLocations
                : suggestedLocations?.destinations
            : undefined;
        }),
        map((locations: Location[]) => locations?.map((location) => ({ ...location, isDefault: true })))
      )
    ).pipe(finShare());

    this.enablePersonalization$ = this.consentService
      .getCookieConsentStatusById(ConsentTextId.COOKIE_PERSONALIZATION)
      .pipe(distinctUntilChanged());

    this.queryString$ = this.locationSearchForm
      .get('locationSearch')
      .valueChanges.pipe(debounceTime(500), distinctUntilChanged(), startWith(''), finShare());

    this.hasLocations$ = combineLatest([this.locationResults$, this.defaultLocations$]).pipe(
      map(([results, defaults]) => isPresent(results) || isPresent(defaults))
    );

    this.subscriptions.add(
      this.queryString$.pipe(filter(isNotBlank)).subscribe((queryString) => {
        this.GtmLocationEventType$.next(`${isBlank(queryString) ? 'locations' : 'suggestions'}`);
        this.sendGtmEvent(GtmLocationEventType.INPUT, queryString);
      })
    );

    this.subscriptions.add(
      this.queryString$
        .pipe(
          filter(isNotBlank),
          withLatestFrom(this.locationType$),
          switchMap(([queryString, locationType]) => {
            if (this.isAm()) {
              return this.searchAmHolidays(queryString, locationType);
            }

            const continent =
              locationType === 'origin' ? this.allowedOriginContinent() : this.allowedDestinationContinent();
            return this.searchFlights(queryString, continent);
          })
        )
        .subscribe((locations: Location[]) => {
          this.locationResults$.next(locations);
        })
    );

    this.subscriptions.add(
      this.queryString$.subscribe(() => {
        if (!this.isAm()) {
          this.locationResults$.next(undefined);
        }

        this.activeIndex$.next(undefined);
      })
    );

    this.isLoading$ = combineLatest([this.queryString$, this.locationResults$]).pipe(
      map(([queryString, results]) => isNotBlank(queryString) && !isPresent(results))
    );

    this.subscriptions.add(
      // Read locationPair() synchronously rather than withLatestFrom(bridge): the toObservable bridge
      // emits on the effect flush, which would lag a switch fired in the same tick as the selection.
      this.locationsSwitched.subscribe(() => {
        const { origin, destination } = this.locationPair();
        this.setLocations.emit({
          locations: { origin: destination, destination: origin },
          locationType: [LocationType.DESTINATION, LocationType.ORIGIN],
        });

        if (this.isGlobalBookingWidget()) {
          this.startSearch.emit();
          this.bookingWidgetGtmService.trackElementEvent('global-widget-start-new-search-from-location-switch');
        }
      })
    );

    this.subscriptions.add(
      this.locationSelected.subscribe(({ location, locationType }) => {
        const flight = this.locationPair();
        const oppositeLocationType = locationType === 'origin' ? 'destination' : 'origin';

        const newLocations =
          location.locationCode === flight[oppositeLocationType]?.locationCode
            ? {
                [locationType]: location,
                [oppositeLocationType]: undefined,
              }
            : {
                ...flight,
                [locationType]: location,
              };

        this.sendGtmEvent(GtmLocationEventType.SELECTION, location.locationCode);
        this.setLocations.emit({ locations: newLocations, locationType: [locationType] });
        this.handleClose(false);
      })
    );

    this.subscriptions.add(
      this._modifyType$
        .pipe(
          filter(Boolean),
          filter((modifyType) => modifyType === SelectorType.ORIGIN || modifyType === SelectorType.DESTINATION)
        )
        .subscribe((modifyType) => {
          this.handleOpen(modifyType as keyof Locations);
        })
    );

    //set default location
    if (!this.isGlobalBookingWidget() && !this.skipDefaultOriginFetch()) {
      this.bookingWidgetFlightService.fetchUserLocationAndSetDefaultOrigin();
    }

    this.locations$ = combineLatest([this.queryString$, this.locationResults$, this.defaultLocations$]).pipe(
      switchMap(([queryString, locationResults, defaultLocations]: [string, Location[], Location[]]) =>
        iif((): boolean => queryString?.length > 0, of(locationResults), of(defaultLocations))
      )
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.activeIndex$
        .pipe(filter((i) => isPresent(i) && isPresent(this.locationItems()[i]?.nativeElement)))
        .subscribe((i) => {
          this.locationItems()[i]?.nativeElement.focus();
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  handleInitialLocationItemFocus(index: number): void {
    if (!isPresent(this.activeIndex$.getValue())) {
      this.activeIndex$.next(index);
    }
  }

  handleOpen(type: keyof Locations): void {
    if (!this.bookingWidgetService.usePopoverSelectors()) {
      this.modalOpen = true;
    }

    this.locationType$.next(type);
    this.locationSearchForm.reset({ locationSearch: '' }, { emitEvent: false });
  }

  handleClose(logEvent = true): void {
    this.modalOpen = false;
    this.popoverService.closeByContext(BOOKING_WIDGET_CONTEXT);
    this.resetSearch();
    if (logEvent) {
      this.sendGtmEvent(GtmLocationEventType.CLOSE, this.locationSearchForm.get('locationSearch').value);
    }
  }

  closePopover(): void {
    this.popoverService.close(true);
  }

  resetSearch(): void {
    this.locationSearchForm.controls.locationSearch.setValue('');
    this.locationResults$.next(undefined);
    this.activeIndex$.next(undefined);
  }

  setLocationOrStartNewSearch(location: Location, locationType: LocationType): void {
    this.locationSelected.emit({ location, locationType });

    if (this.isGlobalBookingWidget()) {
      this.startSearch.emit();
      this.bookingWidgetGtmService.trackElementEvent('global-widget-start-new-search-from-location');
    }
  }

  moveFocusToResults(e: Event): void {
    stopPropagation(e);
    this.locationListHeading()?.nativeElement?.focus();
  }

  moveFocusToSearchInput(): void {
    this.searchInput()?.inputElement()?.nativeElement?.focus();
  }

  getUserLocation(): void {
    this.subscriptions.add(
      this.geoLocationService
        .getGeolocationFromNavigator(NEVER)
        .pipe(take(1))
        .subscribe((location: Location) => {
          this.handleClose();
          this.setLocations.emit({ locations: { origin: location }, locationType: [LocationType.ORIGIN] });
        })
    );
  }

  handleKeyDown(e: KeyboardEvent, locations: Location[]): void {
    const currentActiveIndex = this.activeIndex$.getValue();
    const maxIndex = locations ? locations.length - 1 : 0;

    switch (e.key) {
      case 'Down':
      case 'ArrowDown':
        stopPropagation(e);
        this.activeIndex$.next(currentActiveIndex + 1 > maxIndex ? 0 : currentActiveIndex + 1);
        break;
      case 'Up':
      case 'ArrowUp':
        stopPropagation(e);
        this.activeIndex$.next(currentActiveIndex - 1 < 0 ? maxIndex : currentActiveIndex - 1);
        break;
      default:
        break;
    }
  }

  private searchFlights(queryString: string, continent: string): Observable<Location[]> {
    return isBlank(queryString)
      ? of(undefined)
      : this.locationRouteCffService
          .locationMatchesFor(queryString, this.languageService.localeValue, undefined, false, continent)
          .pipe(
            map((data: LocationMatchData) =>
              this.airportsOnly()
                ? data.items.filter((location) => location.type === LocationTypeCff.AIRPORT)
                : data.items
            )
          );
  }

  private searchAmHolidays(queryString: string, locationType: string): Observable<Location[]> {
    return isBlank(queryString)
      ? of(undefined)
      : iif(() => locationType === 'origin', this.amOrigins$, this.amDestinations$).pipe(
          map((amLocations) => this.searchAmLocations(queryString, amLocations))
        );
  }

  private searchAmLocations(query: string, locations: Location[]): Location[] {
    const lcQuery = query.toLowerCase();
    if (locations.some((location) => location.title.toLowerCase().includes(lcQuery))) {
      return locations.sort((a, b) => {
        const lcA = a.title.toLowerCase();
        const lcB = b.title.toLowerCase();

        if (!lcA.includes(lcQuery) && !lcB.includes(lcQuery)) {
          return 0;
        } else if (lcA.includes(lcQuery) && !lcB.includes(lcQuery)) {
          return -1;
        } else if (lcB.includes(lcQuery) && !lcA.includes(lcQuery)) {
          return 1;
        }

        return lcA.indexOf(lcQuery) - lcB.indexOf(lcQuery);
      });
    }

    // levenshtein algorithm from old widget doesn't work properly with exact matches like "lon" or "oul"
    // but it works fine for cases when there is no exact match like "oulo"
    return locations.sort(locationLevenshteinPartialMatchSort(lcQuery));
  }

  private sendGtmEvent(eventType: GtmLocationEventType, state: string): void {
    this.bookingWidgetGtmService.trackElementEvent(
      `${this.locationType$.getValue()}-${this.GtmLocationEventType$.getValue()}-modal-${eventType}`,
      state
    );
  }

  private mapDirectDestinations(destinations: DestinationSearchItem[]): Location[] {
    return destinations.map(
      ({ locationCode, title, countryCode, items, countryMaster }) =>
        ({
          locationCode,
          title,
          cityName: title,
          countryCode,
          type: items?.length ? 'city' : 'airport',
          country: countryMaster,
        }) as Location
    );
  }
}

/**
 * (not so) Amazing sorting algorithm (copied from old widget) to always find the location you wanted!
 * Uses Levenshtein distance but prioritizes case-insensitive partial matches.
 */
export const locationLevenshteinPartialMatchSort =
  (query: string) =>
  (a: Location, b: Location): number =>
    levenshteinDistance(query, a.title) +
    levenshteinDistance(query, a.country) -
    (levenshteinDistance(query, b.title) + levenshteinDistance(query, b.country)) -
    (partialMatch(a.title, query) ? 100 : 0) -
    (partialMatch(a.country, query) ? 100 : 0) +
    (partialMatch(b.country, query) ? 100 : 0) +
    (partialMatch(b.title, query) ? 100 : 0);

const partialMatch = (a: string, b: string): boolean => a.toLowerCase().includes(b.toLowerCase());

const levenshteinDistance = (a = '', b = ''): number => {
  const distanceMatrix = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i += 1) {
    distanceMatrix[0][i] = i;
  }

  for (let j = 0; j <= b.length; j += 1) {
    distanceMatrix[j][0] = j;
  }

  for (let j = 1; j <= b.length; j += 1) {
    for (let i = 1; i <= a.length; i += 1) {
      const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
      distanceMatrix[j][i] = Math.min(
        distanceMatrix[j][i - 1] + 1, // deletion
        distanceMatrix[j - 1][i] + 1, // insertion
        distanceMatrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }

  return distanceMatrix[b.length][a.length];
};
